/**
 * Admin Service
 * Handles admin-specific operations and checks
 */

import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  startAfter,
  writeBatch,
  setDoc,
  addDoc
} from 'firebase/firestore';
import { db } from '../../../services/firebase/firebase';
import { createResourceNotification } from '../../../services/firebase/notificationService';

/**
 * Check if current user is admin
 * 
 * @param {string} userId - User ID to check
 * @returns {Promise<boolean>} Whether user is admin
 */
export const isUserAdmin = async (userId) => {
  try {
    if (!userId) return false;
    
    // Check in companyProfiles collection for isAdmin flag (based on your DB structure)
    const companyRef = doc(db, 'companyProfiles', userId);
    const companySnap = await getDoc(companyRef);
    
    if (companySnap.exists()) {
      const companyData = companySnap.data();
      return companyData.isAdmin === true;
    }
    
    return false;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

/**
 * Get comprehensive dashboard statistics using existing services
 * 
 * @returns {Promise<Object>} Dashboard statistics
 */
export const getDashboardStats = async () => {
  try {
    const stats = {
      users: {
        total: 0,
        activeToday: 0,
        newThisMonth: 0,
        byUniversity: {}
      },
      companies: {
        total: 0,
        verified: 0,
        pending: 0,
        byIndustry: {}
      },
      jobs: {
        total: 0,
        active: 0,
        newThisMonth: 0,
        applications: 0
      },
      resources: {
        total: 0,
        pending: 0,
        active: 0,
        byType: {}
      },
      engagement: {
        totalPosts: 0,
        totalConnections: 0,
        totalMessages: 0,
        dailyActiveUsers: 0
      }
    };

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    // Get user statistics from profiles collection
    try {
      const usersSnapshot = await getDocs(collection(db, 'profiles'));
      stats.users.total = usersSnapshot.size;
      
      usersSnapshot.forEach(doc => {
        const userData = doc.data();
        
        // Count by university
        const university = userData.academic?.university || 'Unknown';
        stats.users.byUniversity[university] = (stats.users.byUniversity[university] || 0) + 1;
        
        // Count new users this month
        if (userData.createdAt && userData.createdAt.toDate && userData.createdAt.toDate() > thirtyDaysAgo) {
          stats.users.newThisMonth++;
        } else if (userData.createdAt && new Date(userData.createdAt) > thirtyDaysAgo) {
          stats.users.newThisMonth++;
        }
      });
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }

    // Get company statistics from companyProfiles collection
    try {
      const companiesSnapshot = await getDocs(collection(db, 'companyProfiles'));
      stats.companies.total = companiesSnapshot.size;
      
      companiesSnapshot.forEach(doc => {
        const companyData = doc.data();
        
        // Count verified vs pending
        if (companyData.verified) {
          stats.companies.verified++;
        } else {
          stats.companies.pending++;
        }
        
        // Count by industry
        const industry = companyData.industry || 'Unknown';
        stats.companies.byIndustry[industry] = (stats.companies.byIndustry[industry] || 0) + 1;
      });
    } catch (error) {
      console.error('Error fetching company stats:', error);
    }

    // Get job statistics from jobPostings collection
    try {
      const jobsSnapshot = await getDocs(collection(db, 'jobPostings'));
      stats.jobs.total = jobsSnapshot.size;
      
      jobsSnapshot.forEach(doc => {
        const jobData = doc.data();
        
        if (jobData.status === 'active') {
          stats.jobs.active++;
        }
        
        const createdAt = jobData.createdAt?.toDate ? jobData.createdAt.toDate() : new Date(jobData.createdAt);
        if (createdAt > thirtyDaysAgo) {
          stats.jobs.newThisMonth++;
        }
      });

      // Get job applications count
      const applicationsSnapshot = await getDocs(collection(db, 'jobApplications'));
      stats.jobs.applications = applicationsSnapshot.size;
    } catch (error) {
      console.error('Error fetching job stats:', error);
    }

    // Get resources statistics using your existing resource structure
    try {
      const resourcesSnapshot = await getDocs(collection(db, 'resources'));
      stats.resources.total = resourcesSnapshot.size;
      
      resourcesSnapshot.forEach(doc => {
        const resourceData = doc.data();
        
        if (resourceData.status === 'active') {
          stats.resources.active++;
        } else if (resourceData.status === 'pending') {
          stats.resources.pending++;
        }
        
        const type = resourceData.resourceType || 'Unknown';
        stats.resources.byType[type] = (stats.resources.byType[type] || 0) + 1;
      });
    } catch (error) {
      console.error('Error fetching resource stats:', error);
    }

    // Get engagement statistics
    try {
      const postsSnapshot = await getDocs(collection(db, 'posts'));
      stats.engagement.totalPosts = postsSnapshot.size;

      const connectionsSnapshot = await getDocs(collection(db, 'connections'));
      stats.engagement.totalConnections = connectionsSnapshot.size;

      const conversationsSnapshot = await getDocs(collection(db, 'conversations'));
      stats.engagement.totalMessages = conversationsSnapshot.size;
    } catch (error) {
      console.error('Error fetching engagement stats:', error);
    }

    return stats;
  } catch (error) {
    console.error('Error getting dashboard stats:', error);
    throw error;
  }
};

/**
 * Get all users with pagination and filtering
 * 
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Users data with pagination
 */
export const getUsers = async (options = {}) => {
  try {
    const { 
      limitCount = 20, 
      lastDoc = null,
      university = null,
      sortBy = 'createdAt',
      sortOrder = 'desc',
      searchTerm = null,
      status = null
    } = options;

    let usersQuery = query(
      collection(db, 'profiles'),
      orderBy(sortBy, sortOrder)
    );

    if (university && university !== 'all') {
      usersQuery = query(usersQuery, where('academic.university', '==', university));
    }

    if (status && status !== 'all') {
      if (status === 'banned') {
        usersQuery = query(usersQuery, where('banned', '==', true));
      } else if (status === 'active') {
        usersQuery = query(usersQuery, where('banned', '!=', true));
      }
    }

    if (lastDoc) {
      usersQuery = query(usersQuery, startAfter(lastDoc));
    }
    
    usersQuery = query(usersQuery, limit(limitCount));

    const snapshot = await getDocs(usersQuery);
    const users = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt instanceof Timestamp ? 
        doc.data().createdAt.toDate() : 
        new Date(doc.data().createdAt || Date.now())
    }));

    // Apply client-side search filter if needed
    let filteredUsers = users;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filteredUsers = users.filter(user => 
        user.basicInfo?.name?.toLowerCase().includes(term) ||
        user.basicInfo?.email?.toLowerCase().includes(term) ||
        user.basicInfo?.username?.toLowerCase().includes(term) ||
        user.academic?.university?.toLowerCase().includes(term)
      );
    }

    return {
      users: filteredUsers,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
      hasMore: snapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error getting users:', error);
    throw error;
  }
};

/**
 * Get all companies with pagination and filtering
 * 
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Companies data with pagination
 */
export const getCompanies = async (options = {}) => {
  try {
    const { 
      limitCount = 20, 
      lastDoc = null,
      status = null,
      industry = null,
      sortBy = 'createdAt',
      sortOrder = 'desc',
      searchTerm = null
    } = options;

    let companiesQuery = query(
      collection(db, 'companyProfiles'),
      orderBy(sortBy, sortOrder)
    );

    if (status && status !== 'all') {
      if (status === 'verified') {
        companiesQuery = query(companiesQuery, where('verified', '==', true));
      } else if (status === 'pending') {
        companiesQuery = query(companiesQuery, where('verified', '==', false));
      } else if (status === 'suspended') {
        companiesQuery = query(companiesQuery, where('suspended', '==', true));
      }
    }

    if (industry && industry !== 'all') {
      companiesQuery = query(companiesQuery, where('industry', '==', industry));
    }

    if (lastDoc) {
      companiesQuery = query(companiesQuery, startAfter(lastDoc));
    }
    
    companiesQuery = query(companiesQuery, limit(limitCount));

    const snapshot = await getDocs(companiesQuery);
    const companies = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt instanceof Timestamp ? 
        doc.data().createdAt.toDate() : 
        new Date(doc.data().createdAt || Date.now())
    }));

    // Apply client-side search filter if needed
    let filteredCompanies = companies;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filteredCompanies = companies.filter(company => 
        company.companyName?.toLowerCase().includes(term) ||
        company.email?.toLowerCase().includes(term) ||
        company.industry?.toLowerCase().includes(term) ||
        company.contactName?.toLowerCase().includes(term)
      );
    }

    return {
      companies: filteredCompanies,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
      hasMore: snapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error getting companies:', error);
    throw error;
  }
};

/**
 * Get pending companies for verification
 * 
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Pending companies
 */
export const getPendingCompanies = async (options = {}) => {
  try {
    const { 
      limitCount = 20, 
      lastDoc = null 
    } = options;

    let companiesQuery = query(
      collection(db, 'companyProfiles'),
      where('verified', '==', false),
      where('isAdmin', '!=', true), // Exclude admin accounts
      orderBy('createdAt', 'desc')
    );

    if (lastDoc) {
      companiesQuery = query(companiesQuery, startAfter(lastDoc));
    }
    
    companiesQuery = query(companiesQuery, limit(limitCount));

    const snapshot = await getDocs(companiesQuery);
    const companies = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt instanceof Timestamp ? 
        doc.data().createdAt.toDate() : 
        new Date(doc.data().createdAt || Date.now())
    }));

    return {
      companies,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
      hasMore: snapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error getting pending companies:', error);
    throw error;
  }
};

/**
 * Get company details by ID
 * 
 * @param {string} companyId - Company ID
 * @returns {Promise<Object>} Company details
 */
export const getCompanyDetails = async (companyId) => {
  try {
    const companyRef = doc(db, 'companyProfiles', companyId);
    const companySnap = await getDoc(companyRef);
    
    if (!companySnap.exists()) {
      throw new Error('Company not found');
    }

    const companyData = companySnap.data();
    
    // Get additional stats for this company
    const jobsQuery = query(
      collection(db, 'jobPostings'),
      where('employerId', '==', companyId)
    );
    const jobsSnapshot = await getDocs(jobsQuery);
    
    const activeJobs = jobsSnapshot.docs.filter(doc => 
      doc.data().status === 'active'
    ).length;
    
    // Get applications for this company's jobs
    const jobIds = jobsSnapshot.docs.map(doc => doc.id);
    let totalApplications = 0;
    
    if (jobIds.length > 0) {
      // Batch applications query (Firestore 'in' limit is 10)
      const batchSize = 10;
      for (let i = 0; i < jobIds.length; i += batchSize) {
        const batchJobIds = jobIds.slice(i, i + batchSize);
        const applicationsQuery = query(
          collection(db, 'jobApplications'),
          where('jobId', 'in', batchJobIds)
        );
        const applicationsSnapshot = await getDocs(applicationsQuery);
        totalApplications += applicationsSnapshot.size;
      }
    }

    return {
      id: companySnap.id,
      ...companyData,
      stats: {
        totalJobs: jobsSnapshot.size,
        activeJobs,
        totalApplications
      },
      createdAt: companyData.createdAt instanceof Timestamp ? 
        companyData.createdAt.toDate() : 
        new Date(companyData.createdAt || Date.now())
    };
  } catch (error) {
    console.error('Error getting company details:', error);
    throw error;
  }
};

/**
 * Verify or reject a company
 * 
 * @param {string} companyId - Company ID
 * @param {boolean} verified - Whether to verify or reject
 * @param {string} reason - Reason for rejection (optional)
 * @returns {Promise<void>}
 */
export const updateCompanyVerification = async (companyId, verified, reason = '') => {
  try {
    const companyRef = doc(db, 'companyProfiles', companyId);
    const updateData = {
      verified,
      verificationDate: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    if (!verified && reason) {
      updateData.rejectionReason = reason;
      updateData.status = 'rejected';
    } else if (verified) {
      updateData.status = 'verified';
    }

    await updateDoc(companyRef, updateData);

    // TODO: Create notification for company
    // You can add notification logic here similar to resource notifications
    
  } catch (error) {
    console.error('Error updating company verification:', error);
    throw error;
  }
};

/**
 * Suspend or unsuspend a company
 * 
 * @param {string} companyId - Company ID
 * @param {boolean} suspended - Whether to suspend or unsuspend
 * @param {string} reason - Reason for suspension
 * @returns {Promise<void>}
 */
export const updateCompanySuspension = async (companyId, suspended, reason = '') => {
  try {
    const companyRef = doc(db, 'companyProfiles', companyId);
    const updateData = {
      suspended,
      suspensionDate: suspended ? serverTimestamp() : null,
      suspensionReason: suspended ? reason : null,
      updatedAt: serverTimestamp()
    };

    await updateDoc(companyRef, updateData);

    // If suspending, also deactivate all job postings
    if (suspended) {
      const jobsQuery = query(
        collection(db, 'jobPostings'),
        where('employerId', '==', companyId),
        where('status', '==', 'active')
      );
      const jobsSnapshot = await getDocs(jobsQuery);
      
      const batch = writeBatch(db);
      jobsSnapshot.docs.forEach(doc => {
        batch.update(doc.ref, {
          status: 'suspended',
          suspendedAt: serverTimestamp()
        });
      });
      
      if (jobsSnapshot.docs.length > 0) {
        await batch.commit();
      }
    }
    
  } catch (error) {
    console.error('Error updating company suspension:', error);
    throw error;
  }
};

/**
 * Get pending resources for approval using existing resource service structure
 * 
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Pending resources with pagination
 */
export const getPendingResources = async (options = {}) => {
  try {
    const { 
      limitCount = 20, 
      lastDoc = null
    } = options;

    let resourcesQuery = query(
      collection(db, 'resources'),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );

    if (lastDoc) {
      resourcesQuery = query(resourcesQuery, startAfter(lastDoc));
    }
    
    resourcesQuery = query(resourcesQuery, limit(limitCount));

    const snapshot = await getDocs(resourcesQuery);
    const resources = [];
    
    // Get submitter profile info for each resource
    for (const docSnap of snapshot.docs) {
      const resourceData = docSnap.data();
      
      // Get submitter profile info
      let submitterInfo = {
        name: 'Unknown User',
        university: 'Unknown University'
      };
      
      try {
        if (resourceData.submittedBy) {
          const profileRef = doc(db, 'profiles', resourceData.submittedBy);
          const profileSnap = await getDoc(profileRef);
          
          if (profileSnap.exists()) {
            const profileData = profileSnap.data();
            submitterInfo = {
              name: profileData.basicInfo?.name || 'Unknown User',
              university: profileData.academic?.university || 'Unknown University'
            };
          }
        }
      } catch (profileError) {
        console.warn('Error fetching profile for resource:', profileError);
      }
      
      resources.push({
        id: docSnap.id,
        ...resourceData,
        submittedByName: submitterInfo.name,
        submittedByUniversity: submitterInfo.university,
        createdAt: resourceData.createdAt instanceof Timestamp ? 
          resourceData.createdAt.toDate() : 
          new Date(resourceData.createdAt)
      });
    }

    return {
      resources,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
      hasMore: snapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error getting pending resources:', error);
    throw error;
  }
};

/**
 * Get all resources with filtering (for admin overview)
 * 
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Resources with pagination
 */
export const getAllResources = async (options = {}) => {
  try {
    const { 
      status = null,
      limitCount = 20, 
      lastDoc = null
    } = options;

    let resourcesQuery = query(
      collection(db, 'resources'),
      orderBy('createdAt', 'desc')
    );

    if (status) {
      resourcesQuery = query(resourcesQuery, where('status', '==', status));
    }

    if (lastDoc) {
      resourcesQuery = query(resourcesQuery, startAfter(lastDoc));
    }
    
    resourcesQuery = query(resourcesQuery, limit(limitCount));

    const snapshot = await getDocs(resourcesQuery);
    const resources = [];
    
    // Get submitter profile info for each resource
    for (const docSnap of snapshot.docs) {
      const resourceData = docSnap.data();
      
      // Get submitter profile info
      let submitterInfo = {
        name: 'Unknown User',
        university: 'Unknown University'
      };
      
      try {
        if (resourceData.submittedBy) {
          const profileRef = doc(db, 'profiles', resourceData.submittedBy);
          const profileSnap = await getDoc(profileRef);
          
          if (profileSnap.exists()) {
            const profileData = profileSnap.data();
            submitterInfo = {
              name: profileData.basicInfo?.name || 'Unknown User',
              university: profileData.academic?.university || 'Unknown University'
            };
          }
        }
      } catch (profileError) {
        console.warn('Error fetching profile for resource:', profileError);
      }
      
      resources.push({
        id: docSnap.id,
        ...resourceData,
        submittedByName: submitterInfo.name,
        submittedByUniversity: submitterInfo.university,
        createdAt: resourceData.createdAt instanceof Timestamp ? 
          resourceData.createdAt.toDate() : 
          new Date(resourceData.createdAt)
      });
    }

    return {
      resources,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
      hasMore: snapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error getting all resources:', error);
    throw error;
  }
};

/**
 * Approve or reject a resource (admin function)
 * 
 * @param {string} resourceId - Resource ID
 * @param {string} status - 'active' or 'rejected'
 * @param {string} reason - Reason for rejection (optional)
 * @returns {Promise<void>}
 */
export const updateResourceStatus = async (resourceId, status, reason = '') => {
  try {
    const resourceRef = doc(db, 'resources', resourceId);
    
    // Get resource data before updating
    const resourceSnap = await getDoc(resourceRef);
    if (!resourceSnap.exists()) {
      throw new Error('Resource not found');
    }
    
    const resourceData = resourceSnap.data();
    
    const updateData = {
      status,
      reviewedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    if (status === 'rejected' && reason) {
      updateData.rejectionReason = reason;
    } else if (status === 'active') {
      updateData.approvedAt = serverTimestamp();
    }

    await updateDoc(resourceRef, updateData);
    
    // Create notification for the user who submitted the resource
    if (resourceData.submittedBy) {
      try {
        await createResourceNotification(
          resourceData.submittedBy,
          resourceData.title,
          status,
          reason
        );
      } catch (notificationError) {
        console.warn('Error creating notification:', notificationError);
        // Don't throw error if notification fails, resource update was successful
      }
    }
    
  } catch (error) {
    console.error('Error updating resource status:', error);
    throw error;
  }
};

/**
 * Get reported content
 * 
 * @returns {Promise<Array>} Reported content items
 */
export const getReportedContent = async () => {
  try {
    // This would get reported posts, comments, etc.
    // For now, return empty array as the reporting system would need to be implemented
    return [];
  } catch (error) {
    console.error('Error getting reported content:', error);
    throw error;
  }
};

/**
 * Get system analytics
 * 
 * @param {string} timeRange - Time range for analytics ('7d', '30d', '90d')
 * @returns {Promise<Object>} Analytics data
 */
export const getSystemAnalytics = async (timeRange = '30d') => {
  try {
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    // Get user growth over time
    const usersSnapshot = await getDocs(collection(db, 'profiles'));
    const userGrowth = {};
    
    usersSnapshot.forEach(doc => {
      const userData = doc.data();
      if (userData.createdAt) {
        const date = userData.createdAt.toDate?.() || new Date(userData.createdAt);
        if (date >= startDate) {
          const dateStr = date.toDateString();
          userGrowth[dateStr] = (userGrowth[dateStr] || 0) + 1;
        }
      }
    });

    // Get job application trends
    const applicationsSnapshot = await getDocs(collection(db, 'jobApplications'));
    const applicationTrends = {};
    
    applicationsSnapshot.forEach(doc => {
      const appData = doc.data();
      if (appData.appliedAt) {
        const date = appData.appliedAt.toDate?.() || new Date(appData.appliedAt);
        if (date >= startDate) {
          const dateStr = date.toDateString();
          applicationTrends[dateStr] = (applicationTrends[dateStr] || 0) + 1;
        }
      }
    });

    // Get engagement metrics
    const postsSnapshot = await getDocs(collection(db, 'posts'));
    const postActivity = {};
    
    postsSnapshot.forEach(doc => {
      const postData = doc.data();
      if (postData.createdAt) {
        const date = postData.createdAt.toDate?.() || new Date(postData.createdAt);
        if (date >= startDate) {
          const dateStr = date.toDateString();
          postActivity[dateStr] = (postActivity[dateStr] || 0) + 1;
        }
      }
    });

    return {
      userGrowth,
      applicationTrends,
      postActivity,
      timeRange
    };
  } catch (error) {
    console.error('Error getting system analytics:', error);
    throw error;
  }
};

/**
 * Ban or unban a user
 * 
 * @param {string} userId - User ID
 * @param {boolean} banned - Whether to ban or unban
 * @param {string} reason - Reason for ban
 * @returns {Promise<void>}
 */
export const updateUserBanStatus = async (userId, banned, reason = '') => {
  try {
    const userRef = doc(db, 'profiles', userId);
    const updateData = {
      banned,
      banDate: banned ? serverTimestamp() : null,
      banReason: banned ? reason : null,
      updatedAt: serverTimestamp()
    };

    await updateDoc(userRef, updateData);
  } catch (error) {
    console.error('Error updating user ban status:', error);
    throw error;
  }
};

/**
 * Delete user account and associated data
 * 
 * @param {string} userId - User ID to delete
 * @returns {Promise<void>}
 */
export const deleteUserAccount = async (userId) => {
  try {
    const batch = writeBatch(db);

    // Delete user profile
    const userRef = doc(db, 'profiles', userId);
    batch.delete(userRef);

    // Delete user posts
    const postsQuery = query(collection(db, 'posts'), where('authorId', '==', userId));
    const postsSnapshot = await getDocs(postsQuery);
    postsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // Delete user resources
    const resourcesQuery = query(collection(db, 'resources'), where('submittedBy', '==', userId));
    const resourcesSnapshot = await getDocs(resourcesQuery);
    resourcesSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    await batch.commit();
  } catch (error) {
    console.error('Error deleting user account:', error);
    throw error;
  }
};

export default {
  isUserAdmin,
  getDashboardStats,
  getUsers,
  getCompanies,
  getPendingCompanies, // NEW
  getCompanyDetails, // NEW
  updateCompanyVerification,
  updateCompanySuspension, // NEW
  getPendingResources,
  getAllResources,
  updateResourceStatus,
  getReportedContent,
  getSystemAnalytics,
  updateUserBanStatus,
  deleteUserAccount
};